/* Copyright (c) 2020-present CloudIO Inc */

import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Markdown';

const MarkdownManager: ComponentManagerFn<'Markdown'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'Markdown'>(platform, 'Markdown'),
    type: 'Markdown',
    name: 'Markdown',
    defaultValue,
    props: {
      markdown: {
        label: 'Markdown',
        type: 'string',
        multiline: true,
      },
    },
    category: 'Dashboard',
    icon: { iconName: 'markdown', iconType: 'brands' },
    description: `**Overview**<br/>
The Markdown component is a dedicated read-only viewer for rendering formatted text. Instead of writing complex HTML or configuring multiple text widgets, you can simply paste standard Markdown syntax, and the component will automatically transform it into a structured, styled UI.
This is the standard tool for displaying static documentation, help instructions, terms of service, or developer notes (like JSON snippets) directly within your application interface.

**1. Configuration**<br/>
The component is intentionally streamlined to focus on content delivery. It requires only one property to function.
* **Markdown:** A multi-line text field where you enter your content.
* **Syntax Support:** It accepts all standard Markdown formatting conventions.
    * \`# Heading\` → Large Header
    * \`**Bold**\` → Bold Text
    * \`- List Item\` → Bullet point
    * \`> Quote\` → Blockquote style
    * \`\`\`code\`\`\` → Monospaced code block

**2. Layout & Sizing**<br/>
The Markdown component behaves like a standard block-level element.
* **Height:** Automatically expands vertically to fit the length of the rendered text.
* **Width:** Adapts to the width of its parent container (e.g., if placed inside a Card, it fills the Card).

**3. Use Cases**<br/>
* **In-App Documentation:** Create a "Help" tab in your dashboard that explains how to use the features.
* **API Examples:** Display code snippets or configuration JSON for developers to copy.
* **Legal Text:** Render Terms & Conditions or Privacy Policies that are easy to read and maintain.

**4. Limitations**<br/>
* **Static Content Only:** This component does not support dynamic expressions (e.g., you cannot bind it to a variable like $.user.bio). It is strictly for static text defined at design time.
* **Read-Only:** It is not an editor. Users cannot type into it or modify the text.`,
    helpRenders: [
      {
        title: 'Markdown',
        description: 'Sample Markdown',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        height: 420,
        visibleProps: ['markdown'],
        item: {
          itemId: 'a1',
          props: {
            markdown: `
##### Header 5
- One
- Two
- Three

\`\`\`json
{"a": "value"}
\`\`\`

> Quote

[A link](https://google.com)
`,
          },
          children: [],
          type: 'Markdown',
          parent: 'columnPanel1',
        },
      },
    ],
  };
};

export default MarkdownManager;
